/**
 * Dashboard (home). Business-results view over a chosen date range and an
 * optional set of ad accounts (multi-select). Shows:
 *  - business KPI cards (revenue, ad cost, profit, ROAS) from /campaigns/summary,
 *  - secondary count cards (BMs, ad accounts, active campaigns, active rules),
 *  - a spend-vs-revenue line chart for the selected range,
 *  - an AI agent status panel + recent agent logs,
 *  - contextual alerts (expiring tokens, recent agent failures).
 * Each data source is fetched independently (Promise.allSettled) so a single
 * failing endpoint degrades gracefully instead of blanking the whole page.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import Card from '@/components/Cards/Card';
import StatCard from '@/components/Cards/StatCard';
import Select from '@/components/Forms/Select';
import Input from '@/components/Forms/Input';
import MultiSelect from '@/components/Forms/MultiSelect';
import type { SelectOption } from '@/components/Forms/Select';
import LineChart from '@/components/Charts/LineChart';
import Table, { type Column } from '@/components/Tables/Table';
import Badge from '@/components/common/Badge';
import Alert from '@/components/Alerts/Alert';
import Spinner from '@/components/common/Spinner';
import { useToast } from '@/components/Alerts/toastContext';
import * as facebookAccountService from '@/services/facebookAccountService';
import * as adAccountService from '@/services/adAccountService';
import * as campaignService from '@/services/campaignService';
import * as ruleService from '@/services/ruleService';
import * as logService from '@/services/logService';
import type {
  AdAccountDTO,
  AgentDailyReport,
  AgentLogDTO,
  DashboardSummary,
  FacebookAccountDTO,
  OptimizationRuleDTO,
} from '@shared/types';
import { adAccountNumber, formatCurrency, formatDateTime, formatNumber } from '@/utils/formatters';
import { RANGE_OPTIONS, presetToRange, type RangePreset } from '@/utils/dateRange';
import { logStatusMeta } from '@/pages/labels';

const DAY_MS = 24 * 60 * 60 * 1000;

/** "2026-07-25" → "25/07" for the chart axis. */
function shortDay(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

/** Home dashboard view. */
export default function Dashboard(): ReactElement {
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fbAccounts, setFbAccounts] = useState<FacebookAccountDTO[]>([]);
  const [adAccounts, setAdAccounts] = useState<AdAccountDTO[]>([]);
  const [rules, setRules] = useState<OptimizationRuleDTO[]>([]);
  const [logs, setLogs] = useState<AgentLogDTO[]>([]);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [report, setReport] = useState<AgentDailyReport | null>(null);

  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [rangePreset, setRangePreset] = useState<RangePreset>('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = useCallback(async (): Promise<void> => {
    const range =
      rangePreset === 'custom'
        ? { from: customFrom || undefined, to: customTo || undefined }
        : presetToRange(rangePreset);
    if (rangePreset === 'custom' && (!customFrom || !customTo)) return;

    setLoading(true);
    setError(null);
    // Rules/logs are scoped to a single account only when exactly one is chosen.
    const singleAccount = accountIds.length === 1 ? accountIds[0] : undefined;
    try {
      const [fbRes, adRes, ruleRes, logRes, sumRes, reportRes] = await Promise.allSettled([
        facebookAccountService.list(),
        adAccountService.list(),
        ruleService.list(singleAccount ? { adAccountId: singleAccount } : undefined),
        logService.list({ limit: 100, offset: 0, adAccountId: singleAccount }),
        campaignService.summary({ adAccountIds: accountIds, from: range.from, to: range.to }),
        logService.report(),
      ]);

      if (fbRes.status === 'fulfilled') setFbAccounts(fbRes.value);
      if (adRes.status === 'fulfilled') setAdAccounts(adRes.value);
      if (ruleRes.status === 'fulfilled') setRules(ruleRes.value);
      if (logRes.status === 'fulfilled') setLogs(logRes.value.data);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value);
      if (reportRes.status === 'fulfilled') setReport(reportRes.value);

      const allFailed = [fbRes, adRes, ruleRes, logRes, sumRes].every((r) => r.status === 'rejected');
      if (allFailed) setError('Không thể tải dữ liệu bảng điều khiển. Vui lòng thử lại.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đã xảy ra lỗi không xác định');
      toast.error('Không thể tải dữ liệu bảng điều khiển');
    } finally {
      setLoading(false);
    }
  }, [toast, accountIds, rangePreset, customFrom, customTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeRules = useMemo(() => rules.filter((r) => r.isActive), [rules]);
  const adAccountOptions: SelectOption[] = useMemo(
    () => adAccounts.map((a) => ({ value: a.id, label: a.name ?? adAccountNumber(a.adAccountIdFb) })),
    [adAccounts],
  );

  const rangeLabel = useMemo(
    () => RANGE_OPTIONS.find((o) => o.value === rangePreset)?.label ?? '',
    [rangePreset],
  );

  const chartData = useMemo(
    () => (summary?.series ?? []).map((p) => ({ day: shortDay(p.date), spend: p.spend, revenue: p.revenue })),
    [summary],
  );

  const recentLogs = useMemo(() => logs.slice(0, 8), [logs]);
  const actionsLast24h = useMemo(() => {
    const cutoff = Date.now() - DAY_MS;
    return logs.filter((l) => {
      const t = new Date(l.timestamp).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    }).length;
  }, [logs]);
  const failuresLast24h = useMemo(() => {
    const cutoff = Date.now() - DAY_MS;
    return logs.filter((l) => {
      const t = new Date(l.timestamp).getTime();
      return (l.status ?? '').toUpperCase() === 'FAILED' && !Number.isNaN(t) && t >= cutoff;
    }).length;
  }, [logs]);
  const lastActivityAt = logs[0]?.timestamp ?? null;
  const agentActive = actionsLast24h > 0;

  const expiringTokens = useMemo(
    () =>
      fbAccounts.filter((a) => {
        if (!a.tokenExpiresAt) return false;
        const t = new Date(a.tokenExpiresAt).getTime();
        if (Number.isNaN(t)) return false;
        return t - Date.now() < 7 * DAY_MS;
      }),
    [fbAccounts],
  );

  const logColumns: Column<AgentLogDTO>[] = [
    { key: 'timestamp', header: 'Thời gian', render: (row) => formatDateTime(row.timestamp), className: 'align-top' },
    { key: 'eventType', header: 'Sự kiện', className: 'align-top' },
    {
      key: 'description',
      header: 'Mô tả',
      render: (row) => (
        <div className="max-w-[24rem] whitespace-normal break-words">{row.description ?? '—'}</div>
      ),
      className: 'align-top',
    },
    {
      key: 'status',
      header: 'Trạng thái',
      render: (row) => {
        const meta = logStatusMeta(row.status);
        return <Badge color={meta.color}>{meta.label}</Badge>;
      },
      className: 'align-top',
    },
  ];

  const revenue = summary?.revenue ?? 0;
  const spend = summary?.totalSpend ?? 0;
  const profit = summary?.profit ?? 0;
  const roas = summary?.roas ?? null;
  const revenueCaption = summary?.scoped
    ? 'Doanh thu đã thanh toán, đã khớp campaign (theo tài khoản đã chọn)'
    : 'Tổng doanh thu đã thanh toán (mọi route, cả đơn chưa gắn campaign)';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bảng điều khiển</h1>
          <p className="mt-1 text-sm text-gray-500">Kết quả kinh doanh &amp; hoạt động của AI Agent.</p>
        </div>
        <div className="flex w-full flex-wrap items-end gap-3 sm:w-auto">
          <MultiSelect
            label="Tài khoản quảng cáo"
            options={adAccountOptions}
            allLabel="Tất cả tài khoản"
            value={accountIds}
            onChange={setAccountIds}
            wrapperClassName="w-full sm:w-56"
          />
          <Select
            label="Khoảng thời gian"
            options={RANGE_OPTIONS}
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value as RangePreset)}
            wrapperClassName="w-full sm:w-44"
          />
        </div>
      </div>

      {rangePreset === 'custom' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:max-w-md">
          <Input label="Từ ngày" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          <Input label="Đến ngày" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
        </div>
      )}

      {error && <Alert type="error">{error}</Alert>}

      {expiringTokens.length > 0 && (
        <Alert type="warning" title="Token sắp hết hạn">
          Có {expiringTokens.length} tài khoản Facebook có Access Token sắp hết hạn hoặc đã hết hạn.{' '}
          <Link to="/facebook-accounts" className="font-medium underline">
            Kiểm tra ngay
          </Link>
        </Alert>
      )}
      {failuresLast24h > 0 && (
        <Alert type="error" title="Cảnh báo lỗi Agent">
          AI Agent gặp {failuresLast24h} lỗi trong 24 giờ qua.{' '}
          <Link to="/logs" className="font-medium underline">
            Xem nhật ký
          </Link>
        </Alert>
      )}

      {loading && !summary ? (
        <div className="flex items-center justify-center py-24 text-brand-600">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* Daily report — the morning digest (always "today", VN) */}
          {report && (
            <Card
              title={`📊 Báo cáo hôm nay · ${report.date}`}
              actions={<span className="text-xs text-gray-400">{report.model}</span>}
            >
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="flex items-center gap-1.5">
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full ${report.agentActiveToday ? 'bg-green-500' : 'bg-gray-300'}`}
                      aria-hidden="true"
                    />
                    <span className="font-medium text-gray-900">
                      {report.agentActiveToday ? 'Agent đang chạy' : 'Agent chưa hoạt động hôm nay'}
                    </span>
                  </span>
                  {report.lastActivityAt && (
                    <span className="text-gray-500">Gần nhất: {formatDateTime(report.lastActivityAt)}</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">Lợi nhuận hôm nay</div>
                    <div className={`text-lg font-bold ${report.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(report.profit)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">ROAS hôm nay</div>
                    <div
                      className={`text-lg font-bold ${report.roas != null && report.roas >= 1 ? 'text-green-600' : 'text-gray-900'}`}
                    >
                      {report.roas != null ? `${report.roas.toFixed(2)}x` : '—'}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">Agent điều chỉnh</div>
                    <div className="text-lg font-bold text-gray-900">
                      <span className="text-green-600">▲{report.budgetIncreases}</span>{' '}
                      <span className="text-amber-600">▼{report.budgetDecreases}</span>
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">Chi phí AI (ước tính)</div>
                    <div className="text-lg font-bold text-gray-900">${report.aiCostUsd.toFixed(2)}</div>
                  </div>
                </div>

                <p className="text-sm text-gray-600">
                  Doanh thu {formatCurrency(report.revenue)} · Chi phí ads {formatCurrency(report.spend)} ·{' '}
                  {formatNumber(report.ordersPaid)} đơn. Agent tăng ngân sách {report.budgetIncreases} lần (
                  {report.daytimeIncreases} vào ban ngày), giảm {report.budgetDecreases} lần; gọi AI ~
                  {formatNumber(report.aiCallsToday)} lần.
                </p>

                {report.errors > 0 && (
                  <Alert type="warning" title={`${report.errors} lỗi hôm nay`}>
                    {report.errorList.slice(0, 2).map((e) => `${e.campaignName}: ${e.message}`).join(' • ')}
                  </Alert>
                )}

                {report.changes.length > 0 ? (
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Thay đổi ngân sách hôm nay
                    </div>
                    <ul className="space-y-1.5">
                      {report.changes.slice(0, 8).map((c, i) => (
                        <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                          <span className={c.direction === 'INCREASE' ? 'text-green-600' : 'text-amber-600'}>
                            {c.direction === 'INCREASE' ? '▲ Tăng' : '▼ Giảm'}
                          </span>
                          <span className="font-medium text-gray-900">{c.campaignName}</span>
                          <span className="text-gray-500">
                            {c.oldValue ? formatCurrency(c.oldValue) : '?'} → {c.newValue ? formatCurrency(c.newValue) : '?'}
                          </span>
                          <span className="text-xs text-gray-400">{String(c.hour).padStart(2, '0')}h</span>
                          {c.rationale && (
                            <span className="w-full text-xs text-gray-400 sm:ml-1 sm:w-auto">— {c.rationale}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {report.changes.length > 8 && (
                      <Link to="/logs" className="mt-2 inline-block text-xs font-medium text-brand-600 hover:text-brand-700">
                        Xem tất cả ({report.changes.length})
                      </Link>
                    )}
                  </div>
                ) : (
                  report.agentActiveToday && (
                    <p className="text-sm text-gray-500">
                      Chưa có thay đổi ngân sách nào hôm nay — agent đang theo dõi, chờ giờ vàng / thời điểm phù hợp.
                    </p>
                  )
                )}
              </div>
            </Card>
          )}

          {/* Business results */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Kết quả kinh doanh
              </h2>
              <span className="text-xs text-gray-400">· {rangeLabel}</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard title="Doanh thu" value={formatCurrency(revenue)} />
              <StatCard title="Chi phí ads" value={formatCurrency(spend)} />
              <StatCard
                title="Lợi nhuận (DT − ads)"
                value={<span className={profit >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrency(profit)}</span>}
              />
              <StatCard
                title="ROAS"
                value={
                  roas != null ? (
                    <span className={roas >= 1 ? 'text-green-600' : 'text-red-600'}>{roas.toFixed(2)}x</span>
                  ) : (
                    '—'
                  )
                }
              />
            </div>
            <p className="mt-2 text-xs text-gray-400">
              {revenueCaption}
              {summary ? ` · ${formatNumber(summary.ordersPaid)} đơn · ${formatNumber(summary.totalConversions)} chuyển đổi` : ''}
              {summary && summary.revenueMatched !== summary.revenue
                ? ` · Đã khớp campaign: ${formatCurrency(summary.revenueMatched)}`
                : ''}
            </p>
          </div>

          {/* Secondary counts */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard title="Tài khoản BM" value={formatNumber(fbAccounts.length)} />
            <StatCard title="Tài khoản quảng cáo" value={formatNumber(adAccounts.length)} />
            <StatCard
              title="Chiến dịch đang chạy"
              value={`${formatNumber(summary?.activeCampaigns ?? 0)} / ${formatNumber(summary?.campaignCount ?? 0)}`}
            />
            <StatCard title="Quy tắc đang bật" value={formatNumber(activeRules.length)} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Spend vs revenue chart */}
            <Card title={`Chi tiêu & doanh thu · ${rangeLabel}`} className="lg:col-span-2">
              <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
                <span>
                  Chi phí ads: <span className="font-semibold text-gray-900">{formatCurrency(spend)}</span>
                </span>
                <span>
                  Doanh thu: <span className="font-semibold text-green-700">{formatCurrency(revenue)}</span>
                </span>
              </div>
              <LineChart
                data={chartData}
                xKey="day"
                series={[
                  { key: 'revenue', label: 'Doanh thu', color: '#16a34a' },
                  { key: 'spend', label: 'Chi phí ads', color: '#2563eb' },
                ]}
                emptyMessage="Chưa có dữ liệu trong khoảng thời gian này."
              />
            </Card>

            {/* Agent status */}
            <Card title="Trạng thái AI Agent">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${agentActive ? 'bg-green-500' : 'bg-gray-300'}`}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    {agentActive ? 'Đang hoạt động' : 'Chờ hoạt động'}
                  </span>
                </div>
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">Hành động (24h)</dt>
                    <dd className="font-semibold text-gray-900">{formatNumber(actionsLast24h)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">Quy tắc đang bật</dt>
                    <dd className="font-semibold text-gray-900">{formatNumber(activeRules.length)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">Tổng ngân sách/ngày (camp đã tiêu)</dt>
                    <dd className="font-semibold text-gray-900">{formatCurrency(summary?.totalDailyBudget ?? 0)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">Chi tiêu ads ({rangeLabel})</dt>
                    <dd className="font-semibold text-gray-900">{formatCurrency(spend)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">Hoạt động gần nhất</dt>
                    <dd className="font-medium text-gray-700">
                      {lastActivityAt ? formatDateTime(lastActivityAt) : '—'}
                    </dd>
                  </div>
                </dl>
              </div>
            </Card>
          </div>

          {/* Recent agent logs */}
          <Card
            title="Nhật ký Agent gần đây"
            actions={
              <Link to="/logs" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                Xem tất cả
              </Link>
            }
          >
            <Table
              columns={logColumns}
              rows={recentLogs}
              rowKey={(row) => row.id}
              emptyMessage="Chưa có hoạt động nào của Agent."
            />
          </Card>
        </>
      )}
    </div>
  );
}
